import {
    ActionReducerMapBuilder,
    createAsyncThunk,
    createSlice,
    SerializedError,
    SliceCaseReducers,
} from '@reduxjs/toolkit';
import { urlUserSettings } from '../../Constants';
import i18n from '../../localization/i18n';
import { IAppReduxStore, IAction, SlicePropsWithData } from '../store';
import { GridDensity } from '@mui/x-data-grid-pro';
import { apiFetch } from '../../util/ProjectUtils';

export enum SidepanelVisibilityEnum {
    expanded = 'expanded',
    collapsed = 'collapsed',
}

/**
 * Timer that limits the number of requests that are sent to the backend when project favourites are changed.
 */
let updateSettingsTimer: number;

/**
 * Initial slice state value.
 */
const initialState: IUserSettingsState = {
    data: {
        language: i18n.language,
        favourites: {
            projects: [],
        },
        gridDensity: 'standard',
        sidepanelVisibility: SidepanelVisibilityEnum.expanded,
        headerFilters: true,
    },
    loading: true,
    error: null,

    showFilter: false,
    hasFilteredItems: false,
    showColumnsMenu: false,
    nextArea: null,
};

/**
 * Collection of the reducers for this slice.
 */
const reducers = {
    /**
     * Updates the "showFilter" property of the state's settings object.
     * @param state - Current slice state object.
     * @param action - Action containing the new document fieldset.
     */
    setShowFilter(state: IUserSettingsState, action: IAction<boolean>) {
        state.showFilter = action.payload;
    },

    setShowColumnsMenu(state: IUserSettingsState, action: IAction<boolean>) {
        state.showColumnsMenu = action.payload;
    },

    setHasFilteredItems(state: IUserSettingsState, action: IAction<boolean>) {
        state.hasFilteredItems = action.payload;
    },

    /**
     * Updates the "nextArea" property of the state's settings object.
     * @param state - Current slice state object.
     * @param action - Action containing the new nextArea value.
     */
    setNextArea(state: IUserSettingsState, action: IAction<string>) {
        state.nextArea = action.payload;
    },
};

/**
 * Collection of the thunks for this slice, along with their reducer functions.
 */
const thunks = {
    /**
     * Fetches the user's settings from the backend.
     */
    getSettings: async (_, thunkApi) => {
        try {
            const response = await apiFetch(urlUserSettings);

            /** Get access to the default settings */
            const settings: IUserSettings = {
                ...userSettingsSlice.getInitialState().data,
            };

            /** HTTP 404 means that the user has no settings - therefore create a default settings object. */
            if (response.status === 404) {
                await thunkApi.dispatch(updateSettings(settings));

                return settings;
            }

            /** Get the string content of the response. */
            const jsonObj = await response.json();

            /** Make sure settings JSON is valid. */
            const settingsJson = cleanSettings(jsonObj, settings);

            /** If something in the settings object has changed, send the changes to the backend. */
            if (JSON.stringify(jsonObj) !== JSON.stringify(settingsJson)) {
                await thunkApi.dispatch(updateSettings(settingsJson));
            }

            return settingsJson;
        } catch (e) {
            Promise.reject(e);
        }
    },

    /**
     * Updates the user's settings in the backend.
     */
    updateSettings: async (settings: IUserSettings) => {
        try {
            /** Note: Backend only accepts data in form-format! */
            const formData = new FormData();
            formData.append('userSettings', JSON.stringify(settings));

            const request: RequestInit = {
                method: 'POST',
                body: formData,
            };
            await apiFetch(urlUserSettings, request);

            return settings;
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    /**
     * Updates users application language setting.
     */
    updateLanguage: async (payload: { language: string }, thunkApi) => {
        try {
            /** Get access to the redux store's settings slice. */
            const settings = (thunkApi.getState() as IAppReduxStore).userSettings.data;

            /** Clear any existing timer. */
            window.clearTimeout(updateSettingsTimer);

            /** Configure timer. */
            updateSettingsTimer = window.setTimeout((): void => {
                const newSettings: IUserSettings = {
                    ...settings,
                    language: payload.language,
                };

                thunkApi.dispatch(updateSettings(newSettings));
            }, 2000);
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    /**
     * Updates the the 'favourite' state of a project.
     */
    updateFavourites: async (payload: { id: string; favourite: boolean }, thunkApi) => {
        try {
            /** Get access to the redux store's settings slice. */
            const settings = (thunkApi.getState() as IAppReduxStore).userSettings.data;

            /** Create a copy of the project favourite array. */
            let projectFavourites: string[] = [...settings.favourites.projects];

            /** Add/Remove the favourite from the Array. */
            if (payload.favourite) {
                projectFavourites.push(payload.id);
            } else {
                projectFavourites = projectFavourites.filter(
                    (x: string): boolean => x !== payload.id,
                );
            }

            /** Clear any existing timer. */
            window.clearTimeout(updateSettingsTimer);

            /** Configure timer. */
            updateSettingsTimer = window.setTimeout((): void => {
                const newSettings: IUserSettings = {
                    ...settings,
                    favourites: {
                        ...settings.favourites,
                        projects: projectFavourites,
                    },
                };

                thunkApi.dispatch(updateSettings(newSettings));
            }, 2000);

            return projectFavourites;
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    /**
     * Updates the 'gridDensity' state.
     */
    updateGridDensity: async (payload: { gridDensity: GridDensity }, thunkApi) => {
        try {
            /** Get access to the redux store's settings slice. */
            const settings = (thunkApi.getState() as IAppReduxStore).userSettings.data;

            /** Create a copy of the gridDensity string. */
            const selectedDensity: GridDensity = payload.gridDensity;

            /** Clear any existing timer. */
            window.clearTimeout(updateSettingsTimer);

            /** Configure timer. */
            updateSettingsTimer = window.setTimeout((): void => {
                const newSettings: IUserSettings = {
                    ...settings,
                    gridDensity: selectedDensity,
                };

                thunkApi.dispatch(updateSettings(newSettings));
            }, 2000);

            return selectedDensity;
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    /**
     * Updates the 'sidepanelVisibility' state.
     */
    updateSidePanelVisibility: async (
        payload: {
            sidepanelVisibility: SidepanelVisibilityEnum;
        },
        thunkApi,
    ) => {
        try {
            /** Get access to the redux store's settings slice. */
            const settings = (thunkApi.getState() as IAppReduxStore).userSettings.data;

            /** Create a copy of the sidepanelVisibility. */
            const selectedSidepanelVisibility: SidepanelVisibilityEnum =
                payload.sidepanelVisibility;

            /** Clear any existing timer. */
            window.clearTimeout(updateSettingsTimer);

            /** Configure timer. */
            updateSettingsTimer = window.setTimeout((): void => {
                const newSettings: IUserSettings = {
                    ...settings,
                    sidepanelVisibility: selectedSidepanelVisibility,
                };

                thunkApi.dispatch(updateSettings(newSettings));
            }, 2000);

            return selectedSidepanelVisibility;
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    /**
     * Updates the 'headerFilters' state.
     */
    updateHeaderFilters: async (payload: { headerFilters: boolean }, thunkApi) => {
        try {
            /** Get access to the redux store's settings slice. */
            const settings = (thunkApi.getState() as IAppReduxStore).userSettings.data;

            /** Create a copy of the headerFilters. */
            const isHeaderFilters: boolean = payload.headerFilters;

            /** Clear any existing timer. */
            window.clearTimeout(updateSettingsTimer);

            /** Configure timer. */
            updateSettingsTimer = window.setTimeout((): void => {
                const newSettings: IUserSettings = {
                    ...settings,
                    headerFilters: isHeaderFilters,
                };

                thunkApi.dispatch(updateSettings(newSettings));
            }, 2000);

            return isHeaderFilters;
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    /**
     * Registers the 'extraReducers' functions for the slice's thunks.
     */
    _reducers: (builder: ActionReducerMapBuilder<IUserSettingsState>) => {
        /**
         * Reducers for the 'getSettings' thunk.
         */
        builder.addCase(getSettings.pending, state => {
            state.loading = true;
        });
        builder.addCase(getSettings.fulfilled, (state, action) => {
            state.data = action.payload;
            state.loading = false;
        });
        builder.addCase(getSettings.rejected, (state, action: IAction<SerializedError>) => {
            state.error = action.error;
            state.loading = false;
        });

        /**
         * Reducers for the 'updateSettings' thunk.
         */
        builder.addCase(updateSettings.rejected, (state, action: IAction<SerializedError>) => {
            state.error = action.error;
        });

        /**
         * Reducers for the 'updateFavourite' thunk.
         */
        builder.addCase(updateFavourites.fulfilled, (state, action) => {
            state.data.favourites.projects = action.payload;
        });
        builder.addCase(updateFavourites.rejected, (state, action: IAction<SerializedError>) => {
            state.error = action.error;
        });

        /**
         * Reducers for the 'updateLanguage' thunk.
         */
        builder.addCase(updateLanguage.fulfilled, (state, action) => {
            state.data.language = action.payload;
        });
        builder.addCase(updateLanguage.rejected, (state, action: IAction<SerializedError>) => {
            state.error = action.error;
        });

        /**
         * Reducers for the 'updateDensity' thunk.
         */
        builder.addCase(updateDensity.fulfilled, (state, action) => {
            state.data.gridDensity = action.payload;
        });
        builder.addCase(updateDensity.rejected, (state, action: IAction<SerializedError>) => {
            state.error = action.error;
        });

        /**
         * Reducers for the 'updateSidepanelVisibility' thunk.
         */
        builder.addCase(
            updateSidepanelVisibility.fulfilled,
            (state: IUserSettingsState, action: IAction<SidepanelVisibilityEnum>) => {
                state.data.sidepanelVisibility = action.payload;
            },
        );
        builder.addCase(
            updateSidepanelVisibility.rejected,
            (state, action: IAction<SerializedError>) => {
                state.error = action.error;
            },
        );

        /**
         * Reducers for the 'updateHeaderFilters' thunk.
         */
        builder.addCase(
            updateHeaderFilters.fulfilled,
            (state: IUserSettingsState, action: IAction<boolean>) => {
                state.data.headerFilters = action.payload;
            },
        );
        builder.addCase(updateHeaderFilters.rejected, (state, action: IAction<SerializedError>) => {
            state.error = action.error;
        });
    },
};

/**
 * Checks to see if two objects have the same properties.
 * @param objA - First object.
 * @param objB - Second object.
 * @returns If the objects have the same properties or not.
 */
const haveSameProperties = <T, X>(objA: T, objB: X): boolean => {
    for (const key in objA) {
        if (!Object.prototype.hasOwnProperty.call(objB, key)) {
            return false;
        }
    }

    for (const key in objB) {
        if (!Object.prototype.hasOwnProperty.call(objA, key)) {
            return false;
        }
    }

    return true;
};

/**
 * Removes properties from a given settings object that do not match the schema of IUserSettings.
 * @param settings - Target settings object.
 * @param original - Reference settings object.
 * @return Updated settings object.
 */
const cleanSettings = (settings: IUserSettings, original: IUserSettings): IUserSettings => {
    Object.keys(settings)
        /** Identify old values that are no longer needed. */
        .filter((x: string): boolean => !Object.prototype.hasOwnProperty.call(original, x))
        /** Remove un-needed properties. */
        .forEach((x: string): boolean => delete settings[x]);

    /** Copy over any missing properties from 'original' with their default values. */
    if (!haveSameProperties(original, settings)) {
        settings = { ...original, ...settings };
    }

    return settings;
};

/**
 * Interface that describes the state object for this redux slice.
 */
export interface IUserSettingsState extends SlicePropsWithData<IUserSettings> {
    /** Whether to show the DocTable's filter controls. */
    showFilter: boolean;

    /** Whether to show the DocTable's columns menu. */
    showColumnsMenu: boolean;

    /** Stores the name of the project area that the user wants to navigate to (used in case user needs to be warned about their document selection being lost.) */
    nextArea: string;

    /** Shows if data is filtered. */
    hasFilteredItems: boolean;
}

/**
 * Interface that describes the settings for a user.
 */
export interface IUserSettings {
    /** User's preferred application language. */
    language: string;

    /** User favourites. */
    favourites: {
        /** User's favourited projects. */
        projects: string[];
    };

    /** Controls the line height of individual rows in the DocTable. */
    gridDensity: GridDensity;

    /** Controls the visibility of the sidepanel. */
    sidepanelVisibility: SidepanelVisibilityEnum;

    /** Controls the header filters of the table's columns. */
    headerFilters: boolean;
}

/**
 * Expose thunk functions as exports.
 */
export const getSettings = createAsyncThunk(`${urlUserSettings}/get`, thunks.getSettings);
export const updateSettings = createAsyncThunk(`${urlUserSettings}/update`, thunks.updateSettings);
export const updateFavourites = createAsyncThunk(
    `${urlUserSettings}/favourites`,
    thunks.updateFavourites,
);
export const updateLanguage = createAsyncThunk(
    `${urlUserSettings}/language`,
    thunks.updateLanguage,
);
export const updateDensity = createAsyncThunk(
    `${urlUserSettings}/density`,
    thunks.updateGridDensity,
);
export const updateSidepanelVisibility = createAsyncThunk(
    `${urlUserSettings}/sidepanel`,
    thunks.updateSidePanelVisibility,
);
export const updateHeaderFilters = createAsyncThunk(
    `${urlUserSettings}/headerFilters`,
    thunks.updateHeaderFilters,
);

/**
 * Create the redux slice.
 */
export const userSettingsSlice = createSlice<
    IUserSettingsState,
    SliceCaseReducers<IUserSettingsState>
>({
    name: 'userSettings',
    initialState: initialState,
    reducers,
    extraReducers: thunks._reducers,
});

/**
 * Export functions from slice actions.
 */
export const { setShowFilter, setShowColumnsMenu, setNextArea, setHasFilteredItems } =
    userSettingsSlice.actions;

/**
 * Export slice reducer as default.
 */
export default userSettingsSlice.reducer;
